export const QUOTE_EVENT = "ads_conversion_Request_quote_1";

const consentRegions = new Set("AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE IS LI NO GB CH".split(" "));
type Choice = "granted" | "denied" | "unanswered";
type ConsentCommand = { region?: string[]; ad_storage?: string; ad_user_data?: string; ad_personalization?: string };
type TrackingWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

export function advertisingAllowed(region: string | null, choice: Choice, optOut: boolean): boolean {
  if (optOut || choice === "denied" || !region || !/^[A-Z]{2}$/.test(region) || region === "XX") return false;
  return !consentRegions.has(region) || choice === "granted";
}

// Read choices at each send, including changes made in another tab. Never treat
// a generic analytics/cookie acceptance as advertising consent.
function readChoice(region: string | null): Choice {
  const trackingWindow = window as TrackingWindow;
  let choice: Choice = "unanswered";
  for (const item of trackingWindow.dataLayer ?? []) {
    if (!item || typeof item !== "object") continue;
    const command = Array.from(item as ArrayLike<unknown>);
    if (command[0] !== "consent" || !["default", "update"].includes(String(command[1]))) continue;
    const settings = command[2] as ConsentCommand | undefined;
    if (!settings || (settings.region && (!region || !settings.region.includes(region)))) continue;
    if (settings.ad_storage === "denied" || settings.ad_user_data === "denied" || settings.ad_personalization === "denied") choice = "denied";
    // Only an explicit visitor update can grant regulated-region permission.
    else if (command[1] === "update" && settings.ad_storage === "granted") choice = "granted";
  }
  try {
    const saved = localStorage.getItem("cookie_consent");
    if (saved === "denied" || saved === "rejected" || saved === "false") return "denied";
    if (saved) {
      const preferences = JSON.parse(saved);
      if (preferences?.advertising === false || preferences?.marketing === false) return "denied";
    }
  } catch { /* Unrecognized legacy preferences never grant permission. */ }
  return choice;
}

let regionLookup: Promise<string | null> | undefined;
function getRegion(): Promise<string | null> {
  regionLookup ??= (async () => {
    try {
      const response = await fetch("/cdn-cgi/trace", { signal: AbortSignal.timeout(2000), credentials: "same-origin" });
      if (!response.ok) return null;
      return (await response.text()).match(/^loc=([A-Z]{2})$/m)?.[1] ?? null;
    } catch { return null; }
  })();
  return regionLookup;
}

export async function quoteTrackingAllowed(): Promise<boolean> {
  const region = await getRegion();
  const privacyNavigator = navigator as Navigator & { globalPrivacyControl?: boolean };
  return advertisingAllowed(region, readChoice(region), privacyNavigator.globalPrivacyControl === true || navigator.doNotTrack === "1");
}

export function createQuoteReporter(isAllowed: () => Promise<boolean>, send: () => void) {
  const handled = new Set<string>();
  return async (submissionId: string, allowedAtSubmission: boolean): Promise<void> => {
    if (handled.has(submissionId)) return;
    // Claim before any asynchronous work: duplicate mounts cannot race. Blocked
    // submissions are consumed too and never replayed after later acceptance.
    handled.add(submissionId);
    if (!allowedAtSubmission || !(await isAllowed())) return;
    try { send(); } catch { /* Tracking must never interrupt contact delivery. */ }
  };
}

export const reportQuoteSuccess = createQuoteReporter(quoteTrackingAllowed, () => {
  const trackingWindow = window as TrackingWindow;
  if (typeof trackingWindow.gtag === "function") {
    trackingWindow.gtag("event", QUOTE_EVENT, {});
  }
});